import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { getDetailAreaInformation } from "../helpers/hourTime";
import { truncateFloat } from "../helpers/generalHelper";
import { getProjectsByYear } from "../config";

const statusColor = {
    CONCLUIDO: "#85C1E9",
    ACTIVO: "#80E2A9",
    PAUSA: "#C266ff",
    SUSPENDIDO: "#f87681",
};



const useWorkingHourTable = () => {
    const [isLoading, setIsLoading] = useState(true);

    const [columns, setColumns] = useState([]);
    const [projects, setProjects] = useState([]);

    const [sizes, setSizes] = useState();
    const [status, setStatus] = useState();

    const formatProjectTable = useCallback(({ rawProjects, adminTeam, categories }) => {
        const filterStatus = [];
        const filterSize = [];
        const year = rawProjects[0] ? new Date(rawProjects[0].startedDate).getFullYear() : "";

        const projects = rawProjects.map((project, index) => getDetailAreaInformation({ index, project, year }));

        projects.forEach(project => {
            const { size, status } = project;
            filterStatus.push(status)
            filterSize.push(size)
        })

        setProjects(projects);
        setSizes(filterSize);
        setStatus(filterStatus);
    }, []);

    const updateProjects = useCallback(
        async (year) => {
            setIsLoading(true);
            try {
                const { payload } = await getProjectsByYear(year);
                const { projects, adminTeam, categories } = payload;
                formatProjectTable({ rawProjects: projects, adminTeam, categories });
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        },
        [formatProjectTable]
    );

    const updateColumns = useCallback(() => {
        if (!projects || projects?.length === 0) return;
        const projectNames = projects.map(({ name }) => ({ text: name, value: name }));
        const statusFilter = [...new Set(status)].map((status) => ({ text: status, value: status }));
        const sizeFilter = [...new Set(sizes)].map((size) => ({ text: size, value: size }));

        setColumns([
            {
                label: "Categoría(s)",
                title: "Categoría(s)",
                dataIndex: "categoryNames",
                key: "categoryNames",
                onFilter: (value, record) => record.categoryNames.indexOf(value) === 0,
                width: "130px",
            },
            {
                label: "Nombre de Proyecto",
                title: "Nombre de Proyecto",
                dataIndex: "name",
                key: "name",
                sorter: (a, b) => parseInt(b.name.charCodeAt(0)) - parseInt(a.name.charCodeAt(0)),
                width: "250px",
                filterSearch: true,
                onFilter: (value, record) => record.name.includes(value),
                filters: projectNames,
            },
            {
                label: "Estatus",
                title: "Estatus",
                dataIndex: "status",
                key: "status",
                onFilter: (value, record) => record.status.indexOf(value) === 0,
                width: "120px",
                filters: statusFilter,
                filterSearch: true,
                render: (value) => (
                    <div
                        className="text-[10px] text-white font-semibold leading-normal h-5 p-[5px] rounded-md flex justify-center items-center"
                        style={{ background: statusColor[value] }}
                    >
                        {value}
                    </div>
                ),
            },
            {
                label: "Tamaño de Proyecto",
                title: "Tamaño de Proyecto",
                dataIndex: "size",
                key: "size",

                width: "110px",
                filterSearch: true,
                onFilter: (value, record) => record.size === value,
                filters: sizeFilter,
            },
            {
                label: "Horas de presupuesto desarrollo",
                title: "Horas de presupuesto desarrollo",
                dataIndex: "totalDevelopment",
                key: "totalDevelopment",
                width: "125px",
            },
            { label: "Hrs Assets", title: "Hrs Assets", dataIndex: "assetTime", key: "assetTime", width: "110px" },
            { label: "Hrs Proveedores", title: "Hrs Proveedores", dataIndex: "providerTime", key: "providerTime", width: "125px" },
            { label: "Hrs Servidor", title: "Hrs Servidor", dataIndex: "serverTime", key: "serverTime", width: "115px" },
            { label: "Hrs Planeación", title: "Hrs Planeación", dataIndex: "planning", key: "planning", width: "115px" },
            { label: "Hrs Diseño UI/UX", title: "Hrs Diseño UI/UX", dataIndex: "design", key: "design", width: "115px" },
            { label: "Hrs Arte 3D", title: "Hrs Arte 3D", dataIndex: "art3d", key: "art3d", width: "115px" },
            { label: "Hrs Programación Unity", title: "Hrs Programación Unity", dataIndex: "unity", key: "unity", width: "115px" },
            { label: "Hrs Programación Web", title: "Hrs Programación Web", dataIndex: "web", key: "web", width: "115px" },
            { label: "Hrs Consumo total", title: "Hrs Consumo total", dataIndex: "workingHours", key: "workingHours", width: "115px" },
            {
                label: "Diferencia",
                title: "Diferencia",
                dataIndex: "differenceTime",
                sorter: (a, b) => b.differenceTime - a.differenceTime,
            },
            {
                label: "% Consumo",
                title: "% Consumo",
                dataIndex: "percentage",
                render: (value) => `${value === 'Sin dato' ? "Sin dato" : `${truncateFloat(value || 0)}%`}`,
                sorter: (a, b) => b.percentage - a.percentage,
            },
            { label: "Fecha de inicio de proyecto", title: "Fecha de inicio de proyecto", dataIndex: "startedDate" },
            {
                label: "Fecha de cierre",
                title: "Fecha de cierre",
                dataIndex: "finished",
                sorter: (a, b) => {
                    const dateA = moment(a.finished);
                    const dateB = moment(b.finished);
                    return dateB.isAfter(dateA) ? 1 : dateB.isBefore(dateA) ? -1 : 0;
                },
                render: (data) => data ? moment(data).format("DD/MM/YYYY") : "",
            },
            {
                label: "lideres de proyecto",
                title: "lideres de proyecto",
                dataIndex: "leaders",
                onFilter: (value, record) => record.leaders.indexOf(value) === 0,
            },
            {
                label: "Responsable",
                title: "Responsable",
                dataIndex: "peopleInCharge",
                onFilter: (value, record) => record.peopleInCharge.indexOf(value) === 0,
            },
            {
                label: "Comercial",
                title: "Comercial",
                dataIndex: "peopleInChargeProject",
                onFilter: (value, record) => record.peopleInChargeProject.indexOf(value) === 0,
            },
        ]);
    }, [projects, sizes, status]);

    useEffect(() => {
        updateColumns();
    }, [updateColumns]);

    return { columns, updateProjects, projects, updateColumns, isLoading };
};

export default useWorkingHourTable;
